import React, { useEffect, useState, useMemo } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as clientActions from '../../actions/client';
import * as measuresActions from '../../actions/measures';
import * as adminActions from '../../actions/admin';
import { Button, EButtonType } from '../../components/Button';
import { useHistory, withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { ParameterTypes } from '../../types/parameter.type';
import { MEASURE_STATUSES } from '../../constants/constants';
import { checkIfUserIsAuthorized } from '../Admin/globalFunctions';
import { apiCaller } from '../../utils/apiCaller';
import Select from 'react-select';
import moment from 'moment';
import './createMeasureForm.scss';

import MeasureComments from './MeasureComments';
import MeasureStatusHistory from './MeasureStatusHistory';
import MeasureFileManager from './MeasureFileManager';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import DataTransferComponent from '../../components/DataTransferComponent/DataTransferComponent';

interface Props {
    intl: any;
    loading: boolean;
    client: any;
    clientActions: any;
    measuresActions: any;
    actions: any;
    measures: any;
    permissions: any;
    admin: any;
    buttonLoader: boolean;
    history: any;
    projectDetails: any;
}

const STATUS_VISUAL: Record<number, { color: string; bg: string }> = {
    1: { color: '#3b82f6', bg: '#dbeafe' },
    2: { color: '#22c55e', bg: '#dcfce7' },
    3: { color: '#f59e0b', bg: '#fef3c7' },
};

const STATUS_I18N_KEY: Record<number, string> = {
    1: 'measure.status.initial',
    2: 'measure.status.final',
    3: 'measure.status.pending',
};

const CreateMeasureFormNew = (props: Props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });
    const { measureId, clientId } = useParams<ParameterTypes>();
    const history = useHistory();

    const [measureData, setMeasureData] = useState<any>(null);
    const [measureStatus, setMeasureStatus] = useState<any>(null);
    const [productLocation, setProductLocation] = useState('');
    const [measuringDate, setMeasuringDate] = useState('');
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [position, setPosition] = useState<[number, number] | null>(null);

    const [existingFiles, setExistingFiles] = useState<any[]>([]);
    const [newSketches, setNewSketches] = useState<File[]>([]);
    const [newPhotos, setNewPhotos] = useState<File[]>([]);
    const [newPdfs, setNewPdfs] = useState<File[]>([]);
    const [newVideos, setNewVideos] = useState<File[]>([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showRequired, setShowRequired] = useState(false);
    const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);

    const STATUS_OPTIONS = useMemo(() => MEASURE_STATUSES.map((s: any) => ({
        value: s.value,
        label: STATUS_I18N_KEY[s.value]
            ? fm(STATUS_I18N_KEY[s.value], s.label)
            : s.label,
    })), [props.intl]);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => setPosition([pos.coords.latitude, pos.coords.longitude]),
            (err) => console.warn(`GPS error: ${err.message}`),
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
        );
    }, []);

    useEffect(() => {
        props.measuresActions.getAllMeasureTemplates();
        if (clientId) {
            props.clientActions.fetchClient(clientId, 2);
            props.clientActions.getClientProjects(clientId);
        }
    }, [clientId]);

    useEffect(() => {
        if (Object.keys(props.permissions).length !== 0 && !checkIfUserIsAuthorized(props.permissions, props.permissions?.fullPrivileges, props.permissions?.measures)) {
            toast.error(fm('measure.toast.notAuthorized', 'You do not have permission to modify data in this module.'));
            history.push('/dashboard/measures');
        }
    }, [props.permissions]);

    useEffect(() => {
        if (measureId) {
            setLoading(true);
            props.measuresActions.getClientMeasureByProjectId(measureId).then((res: any) => {
                if (res?.data) {
                    const d = res.data;
                    setMeasureData(d);
                    setMeasureStatus(d.status ? { value: d.status.value, label: d.status.label } : null);
                    setProductLocation(d.productLocation || '');
                    setMeasuringDate(d.measureDate ? moment(d.measureDate).format('DD.MM.YYYY') : '');
                    setSelectedProject(d.project ? { value: d.project.value, label: d.project.label } : null);
                    setExistingFiles(d.measureFiles || []);
                }
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [measureId]);

    useEffect(() => {
        if (props?.projectDetails) {
            setSelectedProject({ value: props.projectDetails.id, label: props.projectDetails.projectName });
        }
    }, [props.projectDetails]);

    useEffect(() => {
        if (measureId) props.actions.getAllProjectInfo('', measureId);
    }, [measureId]);

    const handleGetLocation = () => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = `${pos.coords.latitude},${pos.coords.longitude}`;
                setProductLocation(loc);
                setPosition([pos.coords.latitude, pos.coords.longitude]);
            },
            (err) => toast.error(fm('measure.toast.locationFailed', 'Could not get the location.')),
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
        );
    };

    const handleSetDate = () => {
        setMeasuringDate(moment().format('DD.MM.YYYY'));
    };

    const handleLocationChange = (val: string) => {
        if (/[a-zA-Z]/.test(val)) return;
        setProductLocation(val);
    };

    const handleStatusChange = (option: any) => {
        if (option?.value === 2 && !productLocation) {
            toast.error(fm('measure.toast.mustProvideLocation', 'A location is required for a final measure.'));
            return;
        }
        setMeasureStatus(option);
    };

    const [filesLoading, setFilesLoading] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<number[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleDeleteFile = (fileId: number) => {
        setPendingDeleteId(fileId);
        setPendingBulkDeleteIds([]);
        setShowDeleteConfirm(true);
    };

    const handleBulkDeleteFiles = (ids: number[]) => {
        setPendingBulkDeleteIds(ids);
        setPendingDeleteId(null);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setShowDeleteConfirm(false);
        setFilesLoading(true);
        try {
            if (pendingDeleteId != null) {
                await props.actions.removeFile('measure', pendingDeleteId);
                setExistingFiles(prev => prev.filter(f => f.id !== pendingDeleteId));
                setPendingDeleteId(null);
            } else if (pendingBulkDeleteIds.length > 0) {
                for (const id of pendingBulkDeleteIds) {
                    await props.actions.removeFile('measure', id);
                    setExistingFiles(prev => prev.filter(f => f.id !== id));
                }
                setPendingBulkDeleteIds([]);
            }
        } finally {
            setFilesLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setPendingDeleteId(null);
        setPendingBulkDeleteIds([]);
    };

    const handleSave = () => {
        if (!measureStatus?.value) { setShowRequired(true); return; }
        if (!selectedProject?.value) { setShowRequired(true); return; }

        setSaving(true);
        const fd = new FormData();
        fd.append('clientId', props.client?.client?.id);
        if (measureStatus?.value) fd.append('measureStatusId', measureStatus.value);
        if (selectedProject?.value) fd.append('projectId', selectedProject.value);
        if (productLocation) fd.append('productLocation', productLocation);
        if (measuringDate) fd.append('measureDate', measuringDate);
        fd.append('startDate', props.client?.client?.measureDate ? moment(props.client.client.measureDate).format('YYYY-MM-DD') : '');

        newSketches.forEach(f => fd.append('sketch[]', f));
        newPhotos.forEach(f => fd.append('picture[]', f));
        newPdfs.forEach(f => fd.append('measure[]', f));
        newVideos.forEach(f => fd.append('video[]', f));

        const measureRecordId = props.measures?.measureClient?.id || measureData?.id;

        if (measureRecordId) {
            props.measuresActions.editMeasure(measureRecordId, fd).then((res: any) => {
                if (res?.status === 200) {
                    if (res?.data?.inProduction) {
                        toast.error(fm('measure.toast.inProduction', 'This measure has already moved to production.'));
                        setSaving(false);
                        return;
                    }
                    apiCaller('POST', 'modules/create-client-for-next-module', { moduleId: 2, clientId: props.client?.client?.id });
                    toast.info(fm('measure.toast.saved', 'Measure saved.'));
                    history.goBack();
                }
                setSaving(false);
            });
        } else {
            props.measuresActions.createMeasure(fd).then((res: any) => {
                if (res?.data?.unique?.name) {
                    toast.error(fm('measure.toast.projectNotUnique', 'Project name must be unique.'));
                    setSaving(false);
                    return;
                }
                if (measureStatus?.value === 2) {
                    apiCaller('POST', 'modules/create-client-for-next-module', { moduleId: 2, clientId: props.client?.client?.id });
                }
                toast.info(fm('measure.toast.created', 'Measure created.'));
                history.goBack();
                setSaving(false);
            });
        }
    };

    const clientName = `${props.client?.client?.firstnameAndLastname || ''} ${props.client?.client?.lastName || ''}`.trim();
    const projectName = selectedProject?.label || '';
    const statusId = measureStatus?.value;
    const statusVisual = statusId ? STATUS_VISUAL[statusId] : null;
    const statusKey = statusId ? STATUS_I18N_KEY[statusId] : null;
    const measureRecordId = props.measures?.measureClient?.id || measureData?.id;

    const fileCount = existingFiles.length + newSketches.length + newPhotos.length + newPdfs.length + newVideos.length;

    if (loading) return <div className="cmf-loading"><div className="cmf-spinner" /></div>;

    return (
        <div className="cmf-page">
            <div className="cmf-header-wrap">
                <div className="cmf-header" onClick={() => setHeaderDropdownOpen(!headerDropdownOpen)} style={{ cursor: 'pointer' }}>
                    <div className="cmf-header-left">
                        <div className="cmf-header-info">
                            <h1 className="cmf-title">{clientName || fm('measure.list.title', 'Measures')}</h1>
                            {projectName && <span className="cmf-subtitle">{projectName}</span>}
                        </div>
                        {statusVisual && (
                            <span className="cmf-status-badge" style={{ color: statusVisual.color, background: statusVisual.bg }}>
                                {statusKey
                                    ? <FormattedMessage id={statusKey} defaultMessage={measureStatus?.label || '\u2014'} />
                                    : (measureStatus?.label || '\u2014')}
                            </span>
                        )}
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"
                        style={{ transform: headerDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
                {headerDropdownOpen && (
                    <div className="cmf-header-dropdown">
                        <DataTransferComponent clientId={clientId} />
                    </div>
                )}
            </div>

            <div className="cmf-panels">
                <div className="cmf-panel">
                    <div className="cmf-panel-header"><h3><FormattedMessage id="measure.form.dataSection" defaultMessage="Measure data" /></h3></div>
                    <div className="cmf-panel-body">
                        <div className="cmf-fields">
                            <div className="cmf-field-half">
                                <label><FormattedMessage id="measure.form.project" defaultMessage="Project" /></label>
                                <Select className="cmf-react-select" classNamePrefix="react-select"
                                    options={props.client?.clientProjects?.map((p: any) => ({ value: p.id, label: p.projectName })) || []}
                                    value={selectedProject} onChange={setSelectedProject} isDisabled={true} placeholder={fm('measure.form.selectProject', 'Select project...')} />
                                {showRequired && !selectedProject?.value && <span className="cmf-error"><FormattedMessage id="measure.form.fieldRequired" defaultMessage="This field is required." /></span>}
                            </div>
                            <div className="cmf-field-half">
                                <label><FormattedMessage id="measure.form.statusOfMeasure" defaultMessage="Status of measure" /> <span className="cmf-required">*</span></label>
                                <Select className="cmf-react-select" classNamePrefix="react-select"
                                    options={STATUS_OPTIONS}
                                    value={measureStatus?.value ? STATUS_OPTIONS.find(o => o.value === measureStatus.value) || null : null}
                                    onChange={handleStatusChange}
                                    placeholder={fm('measure.form.selectStatus', 'Select status...')} isSearchable={false}
                                    formatOptionLabel={(opt: any) => (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_VISUAL[opt.value]?.color || '#94a3b8' }} />
                                            {opt.label}
                                        </div>
                                    )} />
                                {showRequired && !measureStatus?.value && <span className="cmf-error"><FormattedMessage id="measure.form.fieldRequired" defaultMessage="This field is required." /></span>}
                            </div>
                            <div className="cmf-field-half">
                                <label><FormattedMessage id="measure.form.measureDate" defaultMessage="Measure date" /></label>
                                <div className="cmf-input-with-action">
                                    <input type="text" value={measuringDate} readOnly placeholder="DD.MM.YYYY" className="cmf-input" />
                                    <button type="button" className="cmf-input-action" onClick={handleSetDate} title={fm('measure.form.measureDate', 'Measure date')}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </button>
                                </div>
                                {props.client?.client?.measureDate && (
                                    <span className="cmf-hint"><FormattedMessage id="measure.form.plannedDate" defaultMessage="Planned measure date" />: {moment(props.client.client.measureDate).format('DD.MM.YYYY')}</span>
                                )}
                            </div>
                            <div className="cmf-field-half">
                                <label><FormattedMessage id="measure.form.clientLocation" defaultMessage="Client location" /></label>
                                <div className="cmf-input-with-action">
                                    <input type="text" value={productLocation} onChange={e => handleLocationChange(e.target.value)} placeholder="GPS..." className="cmf-input" />
                                    <button type="button" className="cmf-input-action" onClick={handleGetLocation} title={fm('measure.form.generateLocation', 'Generate current location')}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            {productLocation && (
                                <div className="cmf-field-full">
                                    <div className="cmf-map-preview">
                                        <iframe src={`https://maps.google.com/maps?q=${productLocation}&z=15&output=embed`}
                                            width="100%" height="150" style={{ border: 0, borderRadius: 8 }}
                                            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {measureRecordId && (
                            <div className="cmf-history-section">
                                <div className="cmf-history-title"><FormattedMessage id="measure.form.historySection" defaultMessage="Status history" /></div>
                                <MeasureStatusHistory measureId={measureRecordId} measuresActions={props.measuresActions} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="cmf-panel">
                    <div className="cmf-panel-header">
                        <h3><FormattedMessage id="measure.form.filesSection" defaultMessage="Files" /> {fileCount > 0 && <span className="cmf-section-count">{fileCount}</span>}</h3>
                    </div>
                    <div className="cmf-panel-body cmf-panel-body-flush">
                        {props.measures.measureTemplates.length > 0 && (
                            <div className="cmf-templates-bar">
                                <span className="cmf-templates-label"><FormattedMessage id="measure.form.templates" defaultMessage="Templates" /></span>
                                <div className="cmf-templates-strip">
                                    {props.measures.measureTemplates.map((t: any, i: number) => {
                                        const name = t.nameOfFile || '';
                                        const ext = name.split('.').pop()?.toLowerCase() || '';
                                        const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
                                        const extLabel = ext === 'pdf' ? 'PDF' : ext === 'doc' || ext === 'docx' ? 'DOC' : ext.toUpperCase();
                                        return (
                                            <a key={i} href={t.template} target="_blank" rel="noopener noreferrer" className={`cmf-tpl-thumb ${!isImage ? 'cmf-tpl-doc' : ''}`} title={name}>
                                                {isImage ? (
                                                    <img src={t.template} alt={name} />
                                                ) : (
                                                    <span className="cmf-tpl-ext">{extLabel}</span>
                                                )}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <MeasureFileManager
                            existingFiles={existingFiles}
                            onFilesChange={(s, p, d, v) => { setNewSketches(s); setNewPhotos(p); setNewPdfs(d); setNewVideos(v); }}
                            onDeleteFile={handleDeleteFile}
                            onBulkDelete={handleBulkDeleteFiles}
                            sketches={newSketches} photos={newPhotos} pdfs={newPdfs} videos={newVideos}
                            loading={filesLoading}
                        />

                        <div className="cmf-comments-section">
                            {measureRecordId && (
                                <MeasureComments measureId={measureRecordId} measuresActions={props.measuresActions} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {((!props.permissions?.observer) || props.permissions?.fullPrivileges) && (
                <div className="cmf-save-bar">
                    <Button
                        isLoading={props.buttonLoader || saving}
                        isDisabled={props.buttonLoader || saving}
                        onClick={handleSave}
                        buttonType={EButtonType.PurpleButton}
                        className="cmf-save-btn"
                    >
                        <FormattedMessage id="measure.form.saveBtn" defaultMessage="Save" />
                    </Button>
                </div>
            )}

            <MikaConfirmModal
                isOpen={showDeleteConfirm}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title={fm('measure.deleteConfirm.title', 'Are you sure?')}
                message={
                    pendingDeleteId != null
                        ? props.intl.formatMessage(
                            { id: 'measure.deleteConfirm.single', defaultMessage: 'Do you want to delete "{name}"?' },
                            { name: (() => {
                                const f = existingFiles.find((x: any) => x.id === pendingDeleteId);
                                return f?.pictureName || f?.sketchName || f?.measureName || '';
                            })() }
                        )
                        : props.intl.formatMessage(
                            { id: 'measure.deleteConfirm.bulk', defaultMessage: 'Do you want to delete {count, plural, one {# file} other {# files}}?' },
                            { count: pendingBulkDeleteIds.length }
                        )
                }
                confirmLabel={fm('measure.deleteConfirm.confirmDelete', 'Delete')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        client: state.client,
        measures: state.measures,
        admin: state.admin
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        clientActions: bindActionCreators(clientActions as any, dispatch),
        measuresActions: bindActionCreators(measuresActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(CreateMeasureFormNew)));
